import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, RefreshCcw, ExternalLink, CheckCircle2, AlertCircle,
  AlertTriangle, Loader2, Copy, KeyRound, TrendingUp, TrendingDown, DollarSign,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Button } from "@/components/ui/button";
import {
  fetchProvidersStatus, fetchProvidersCosts,
  fetchProvidersCostHistory, fetchProvidersMargin,
} from "@/lib/api";
import { toast } from "sonner";

const STATUS_ICON = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  error: AlertCircle,
};

const STATUS_STYLES = {
  ok: {
    dot: "bg-terracotta",
    text: "text-terracotta",
    border: "border-terracotta/40",
    label: "Healthy",
  },
  warn: {
    dot: "bg-bone-300",
    text: "text-bone-200",
    border: "border-bone-400/50",
    label: "Watch",
  },
  error: {
    dot: "bg-red-400",
    text: "text-red-300",
    border: "border-red-400/60",
    label: "Action needed",
  },
};

function formatChecked(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

/**
 * AdminProviders — a single-glance dashboard of every paid outside service
 * Umbra depends on. Live-probes each provider so low balances / broken keys
 * / rate limits surface in one place, each with a direct top-up link.
 */
export default function AdminProviders() {
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState(null);
  const [history, setHistory] = useState(null);
  const [margin, setMargin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statusRes, costsRes, historyRes, marginRes] = await Promise.all([
        fetchProvidersStatus(),
        fetchProvidersCosts().catch(() => null),
        fetchProvidersCostHistory().catch(() => null),
        fetchProvidersMargin().catch(() => null),
      ]);
      setData(statusRes);
      if (costsRes) setCosts(costsRes);
      if (historyRes) setHistory(historyRes);
      if (marginRes) setMargin(marginRes);
    } catch (e) {
      console.error(e);
      toast.error("Could not probe providers.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const copyInstructions = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Top-up steps copied.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const overall = data?.overall_status || "ok";
  const OverallIcon = STATUS_ICON[overall] || CheckCircle2;
  const overallStyle = STATUS_STYLES[overall];

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="back-to-admin"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 sm:pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
          Umbra · API Providers
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          The pipes that carry the workbook.
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Every paid outside service Umbra leans on, in one place. Balances, key health, and one
          tap to the top-up screen when the amber tint appears.
        </p>

        {/* Overall status card */}
        {loading ? (
          <div className="flex items-center gap-3 p-8 border border-obsidian-600">
            <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
            <p className="font-sans text-bone-400">Probing every provider…</p>
          </div>
        ) : (
          <>
            <div
              data-testid="providers-overall"
              className={`border ${overallStyle.border} bg-obsidian-800 p-5 sm:p-6 mb-8 flex items-center justify-between gap-4 flex-wrap`}
            >
              <div className="flex items-center gap-4">
                <div className={`h-3 w-3 rounded-full ${overallStyle.dot} shadow-[0_0_10px_currentColor] ${overallStyle.text}`} />
                <div>
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-500 mb-1">
                    Overall
                  </p>
                  <p className={`font-serif text-xl sm:text-2xl ${overallStyle.text} leading-tight flex items-center gap-2`}>
                    <OverallIcon className="h-5 w-5" />
                    {overallStyle.label}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
                  Checked {formatChecked(data?.checked_at)}
                </span>
                <Button
                  onClick={refresh}
                  disabled={refreshing}
                  variant="outline"
                  data-testid="providers-refresh"
                  className="rounded-none border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 mr-2" />
                  )}
                  Re-probe
                </Button>
              </div>
            </div>

            {/* Revenue vs Cost + 30-day trend chart */}
            {(margin || history) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8" data-testid="providers-financials">
                {/* Left: Revenue / Cost / Margin */}
                {margin && (
                  <div className="border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6 space-y-5">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-terracotta" />
                      <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">
                        {margin.window_days}-day margin
                      </p>
                    </div>
                    <div>
                      <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">Revenue</p>
                      <p data-testid="margin-revenue" className="font-serif text-2xl text-bone-100">
                        ${(margin.revenue_cents / 100).toFixed(2)}
                        <span className="text-bone-500 text-sm font-sans uppercase tracking-[0.2em] ml-2">
                          · {margin.charge_count} charges
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">Cost</p>
                      <p data-testid="margin-cost" className="font-serif text-2xl text-bone-300">
                        ${(margin.cost_cents / 100).toFixed(2)}
                      </p>
                    </div>
                    <div className="pt-3 border-t border-obsidian-600">
                      <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">Margin</p>
                      <p
                        data-testid="margin-net"
                        className={`font-serif text-3xl leading-tight ${
                          margin.margin_cents >= 0 ? "text-terracotta" : "text-red-300"
                        }`}
                      >
                        ${(margin.margin_cents / 100).toFixed(2)}
                        <span className="text-lg font-sans uppercase tracking-[0.2em] ml-2 inline-flex items-center gap-1">
                          {margin.margin_cents >= 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <TrendingDown className="h-4 w-4" />
                          )}
                          {margin.margin_pct}%
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Right: 30-day cost trend line */}
                {history && (
                  <div className="lg:col-span-2 border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6">
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-terracotta" />
                        <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500">
                          {history.window_days}-day cost trend
                        </p>
                      </div>
                      <div className="flex items-center gap-4 font-sans text-[0.6rem] uppercase tracking-[0.2em] text-bone-500">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-terracotta" /> Stripe
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-bone-300" /> LLM
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-bone-500" /> Resend
                        </span>
                      </div>
                    </div>
                    <div className="h-56" data-testid="cost-history-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={(history.series || []).map((b) => ({
                            date: b.date.slice(5),  // MM-DD
                            Stripe: b.stripe / 100,
                            LLM: b.llm / 100,
                            Resend: b.resend / 100,
                          }))}
                          margin={{ top: 10, right: 10, left: -12, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="fillStripe" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#B85A44" stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#B85A44" stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="fillLLM" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#E6E1D9" stopOpacity={0.35} />
                              <stop offset="95%" stopColor="#E6E1D9" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="fillResend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#75726E" stopOpacity={0.35} />
                              <stop offset="95%" stopColor="#75726E" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid stroke="#453D49" strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="date"
                            stroke="#75726E"
                            fontSize={10}
                            interval={4}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            stroke="#75726E"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `$${v.toFixed(v < 1 ? 2 : 0)}`}
                          />
                          <Tooltip
                            contentStyle={{
                              background: "#17161A",
                              border: "1px solid #453D49",
                              fontFamily: "Manrope, sans-serif",
                              fontSize: 12,
                            }}
                            formatter={(v) => `$${Number(v).toFixed(2)}`}
                          />
                          <Area type="monotone" dataKey="Stripe" stackId="1" stroke="#B85A44" fill="url(#fillStripe)" />
                          <Area type="monotone" dataKey="LLM" stackId="1" stroke="#E6E1D9" fill="url(#fillLLM)" />
                          <Area type="monotone" dataKey="Resend" stackId="1" stroke="#75726E" fill="url(#fillResend)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cost estimator — rolling 30-day run rate */}
            {costs && (
              <div
                data-testid="cost-estimator"
                className="border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6 mb-8"
              >
                <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-5 w-5 text-terracotta" />
                    <div>
                      <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-500 mb-1">
                        Rolling {costs.window_days}-day cost estimate
                      </p>
                      <p className="font-serif text-2xl sm:text-3xl text-bone-100 leading-tight">
                        ${(costs.total_cents / 100).toFixed(2)}{" "}
                        <span className="text-bone-400 text-lg font-sans uppercase tracking-[0.2em]">
                          {costs.currency}
                        </span>
                      </p>
                    </div>
                  </div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 self-end">
                    Estimate · Stripe fees are real, LLM/email are modeled
                  </p>
                </div>

                <div className="overflow-x-auto -mx-5 sm:-mx-6">
                  <table className="w-full min-w-[560px] font-sans text-sm">
                    <thead>
                      <tr className="border-y border-obsidian-600 bg-obsidian-900/40">
                        <th className="px-5 sm:px-6 py-3 text-left font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Line</th>
                        <th className="px-3 py-3 text-right font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Cost</th>
                        <th className="px-3 py-3 text-left font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Basis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(costs.lines || []).map((line) => (
                        <tr
                          key={line.id}
                          data-testid={`cost-line-${line.id}`}
                          className="border-b border-obsidian-600 last:border-0"
                        >
                          <td className="px-5 sm:px-6 py-3 text-bone-200">{line.label}</td>
                          <td className="px-3 py-3 text-right font-mono text-terracotta">
                            ${(line.cost_cents / 100).toFixed(2)}
                          </td>
                          <td className="px-3 py-3 text-bone-400 text-xs">
                            {line.detail}
                            {line.formula && (
                              <span className="block text-bone-500 mt-1 text-[0.65rem] uppercase tracking-[0.18em]">
                                {line.formula}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Per-provider cards */}
            <div className="space-y-6" data-testid="providers-list">
              {(data?.providers || []).map((p) => {
                const style = STATUS_STYLES[p.status] || STATUS_STYLES.ok;
                const StatusIcon = STATUS_ICON[p.status] || CheckCircle2;
                return (
                  <div
                    key={p.id}
                    data-testid={`provider-card-${p.id}`}
                    className={`border ${style.border} bg-obsidian-800 p-5 sm:p-6`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="mt-1 shrink-0">
                          <KeyRound className={`h-5 w-5 ${style.text}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-3 flex-wrap mb-1">
                            <p className="font-serif text-xl sm:text-2xl text-bone-100 leading-tight">
                              {p.name}
                            </p>
                            <span
                              data-testid={`provider-status-${p.id}`}
                              className={`inline-flex items-center gap-1 font-sans uppercase tracking-[0.2em] text-[0.55rem] px-2 py-1 border ${style.border} ${style.text}`}
                            >
                              <StatusIcon className="h-3 w-3" />
                              {style.label}
                            </span>
                            {!p.configured && (
                              <span className="inline-flex items-center font-sans uppercase tracking-[0.2em] text-[0.55rem] px-2 py-1 border border-red-400/60 text-red-300">
                                Not configured
                              </span>
                            )}
                          </div>
                          <p className="font-serif italic text-bone-400 leading-relaxed text-sm sm:text-base mb-3">
                            {p.role}
                          </p>
                          <p
                            data-testid={`provider-detail-${p.id}`}
                            className={`font-sans text-sm ${style.text} break-words`}
                          >
                            {p.detail}
                          </p>
                          {p.pricing_note && (
                            <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 mt-3">
                              {p.pricing_note}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-obsidian-600 pt-4 mt-4">
                      <p className="font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500 mb-2">
                        Top up · resolve
                      </p>
                      <p className="font-serif italic text-sm text-bone-300 leading-relaxed mb-4">
                        {p.topup_instructions}
                      </p>
                      <div className="flex items-center gap-3 flex-wrap">
                        {p.topup_url && (
                          <a
                            href={p.topup_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`provider-topup-${p.id}`}
                            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" /> Open top-up
                          </a>
                        )}
                        {p.dashboard_url && p.dashboard_url !== p.topup_url && (
                          <a
                            href={p.dashboard_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`provider-dashboard-${p.id}`}
                            className="inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta text-bone-200 hover:text-terracotta font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" /> Dashboard
                          </a>
                        )}
                        <button
                          onClick={() => copyInstructions(p.topup_instructions)}
                          data-testid={`provider-copy-${p.id}`}
                          className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-400 hover:text-terracotta transition-colors min-h-[44px] px-1"
                        >
                          <Copy className="h-3 w-3" /> Copy steps
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
